import { formatAmount, getMonth, isValidMobile, isValidEmail } from "@utils";

export { isValidMobile, isValidEmail };

export const minAmountFromInvoiceRequestType = (customer, type) => {
  const mapper = {
    None: customer.balance || 0,
    Overdue: customer.overdue || 0,
    OverdueAndDueThisMonth:
      (customer.overdue || 0) + (customer.dueThisMonth || 0),
    All: customer.balance || 0,
  };

  return mapper[type] || 0;
};

/**
 * Normalises raw customer data, validates contact details and amount, and
 * returns a flat array that the table UI can render immediately.
 */

export const buildRequests = (customers, amountKey, threshold = 0.3, fixedAmount = null) =>
  customers.map((c) => {
    // Specific invoices carry a caller-resolved total (the display amount), not a bucket sum — use it
    // directly, and exempt the row from the min-amount gate like Custom (the payable resolves at pay time).
    const minimumAmount =
      amountKey === "SpecificInvoices"
        ? Number(fixedAmount) || 0
        : minAmountFromInvoiceRequestType(c, amountKey);

    const amountError =
      amountKey !== "Custom" &&
      amountKey !== "SpecificInvoices" &&
      minimumAmount < threshold;
    const phoneError = !c.phone || !isValidMobile(c.phone);
    const emailError = !c.email || !isValidEmail(c.email);

    return {
      customerId: c.id || "",
      reference: c.accountCode || "",
      customerName: c.name || "",
      creditLimit: c.creditLimit || 0,
      minimumAmount,
      amountError,
      phone: c.phone,
      phoneError,
      email: c.email,
      emailError,
      balance: c.balance || 0,
      overdue: c.overdue || 0,
      dueThisMonth: c.dueThisMonth || 0,
      paymentProfileId: c.paymentProfileId ?? null,
    };
  });

/* --------------------------------------------------------------------------- */

/**
 * Decides the pre‑flight status for a request row based on the selected channel
 * strategy.
 *
 * Possible resulting status values:
 *   'amountTooSmall' | 'noPhone' | 'noEmail' | 'noContactDetails' | 'ready'
 *   | 'done' | 'notSent' | 'sending' | 'sendingFailed'
 */

export const evaluateStatus = (row, { sms, email }) => {
  if (row.amountError) return "amountTooSmall";
  if (sms && email)
    return row.phoneError && row.emailError ? "noContactDetails" : "ready";
  if (sms) return row.phoneError ? "noPhone" : "ready";
  if (email) return row.emailError ? "noEmail" : "ready";
  return "notSent";
};

export const statusLabel = (status) => {
  switch (status) {
    case "amountTooSmall":
      return "Amount too small";
    case "noPhone":
      return "No phone";
    case "noEmail":
      return "No email";
    case "noContactDetails":
      return "No contact details";
    case "ready":
      return "Ready";
    case "sending":
      return "Sending";
    case "done":
      return "Sent";
    case "notSent":
      return "Not sent";
    case "sendingFailed":
      return "Failed";
    default:
      return status;
  }
};

/* --------------------------------------------------------------------------- */

/**
 * Lightweight concurrency‑limiting helper. Creates at most `limit` promises in
 * flight at any one time.
 */

export const pMap = (items, limit, fn) =>
  new Promise((resolve, reject) => {
    const out = new Array(items.length);
    let i = 0;
    let finished = 0;

    const launch = () => {
      if (i >= items.length) return;
      const currentIndex = i++;
      Promise.resolve(fn(items[currentIndex], currentIndex))
        .then((res) => {
          out[currentIndex] = res;
        })
        .catch(reject)
        .finally(() => {
          finished++;
          if (finished === items.length) return resolve(out);
          launch();
        });
    };

    const pool = Math.min(limit, items.length);
    for (let j = 0; j < pool; j++) launch();
  });

export const calcMaxRequestableAmount = (rows, smsSelected, emailSelected) => {
  if (!rows || !rows.length) return 0;
  const valid = rows.filter((r) => !r.amountError);

  if (smsSelected && emailSelected) {
    return valid
      .filter((r) => !r.phoneError || !r.emailError)
      .reduce((s, r) => s + r.minimumAmount, 0);
  }
  if (smsSelected)
    return valid
      .filter((r) => !r.phoneError)
      .reduce((s, r) => s + r.minimumAmount, 0);
  if (emailSelected)
    return valid
      .filter((r) => !r.emailError)
      .reduce((s, r) => s + r.minimumAmount, 0);
  return 0;
};

export const calcMaxRequestableRows = (rows, smsSelected, emailSelected) => {
  if (!rows || !rows.length) return 0;
  const valid = rows.filter((r) => !r.amountError);

  if (smsSelected && emailSelected) {
    return valid
      .filter((r) => !r.phoneError || !r.emailError)
      .reduce((s, r) => s + 1, 0);
  }
  if (smsSelected)
    return valid.filter((r) => !r.phoneError).reduce((s, r) => s + 1, 0);
  if (emailSelected)
    return valid.filter((r) => !r.emailError).reduce((s, r) => s + 1, 0);
  return 0;
};

/* ---------------------------- replace tokens for message ----------------------*/

export const getCurrentInvoicesRequestedTotal = (row, invoicesRequested) => {
  if (!row) return 0;
  const valueMap = {
    Overdue: row.overdue || 0,
    OverdueAndDueThisMonth: (row.overdue || 0) + (row.dueThisMonth || 0),
    All: row.balance || 0,
    None: row.balance || 0,
  };
  return valueMap[invoicesRequested];
};

export const buildTokensForRow = (row, amountRequested, invoicesRequested) => [
  { token: "customer_name", value: row.customerName },
  { token: "amount_requested", value: amountRequested },
  { token: "current_month", value: getMonth() },
  { token: "previous_month", value: getMonth(-1) },
  { token: "two_months_ago", value: getMonth(-2) },
  { token: "account_balance", value: formatAmount(row.balance) },
  { token: "overdue", value: formatAmount(row.overdue) },
  { token: "overdue_and_due_this_month", value: formatAmount((row.overdue || 0) + (row.dueThisMonth || 0)) },
  { token: "credit_limit", value: formatAmount(row.creditLimit) },
  { token: "invoices_requested", value: invoicesRequested },
  { token: "account_code", value: row.reference },
];
